import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { SubFolderDialogComponent } from '@components/dialog/sub-folder-dialog/sub-folder-dialog.component';
import {
  AttachmentFolderDetailDto,
  AttachmentFolderItemDto,
  AttachmentFolderMergeDto,
  AttachmentFolderService,
} from '@artemis-software/wr-api';
import { PendingChanges } from '@guards/pending-changes.guard';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { queryParam } from '@/utils/signals/router';
import { CurrentUserService } from '@services/current-user.service';

@Component({
  standalone: false,
  selector: 'wr-attachment-folder-tree-detail-page',
  templateUrl: './attachment-folder-detail-page.component.html',
  styleUrls: ['./attachment-folder-detail-page.component.scss'],
})
export class AttachmentFolderDetailPageComponent implements PendingChanges {
  private readonly router = inject(Router);
  private readonly dialogService = inject(NbDialogService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly attachmentFolderService = inject(AttachmentFolderService);
  private readonly toastrService = inject(NbToastrService);

  readonly isNew = computed(() => this.id && this.id === 'new');
  readonly canEdit = computed(() => this.currentUserService.hasPrivilege('editAttachmentFolders'));
  readonly tenantId = queryParam('tenantId', false);
  readonly folderModuleType = queryParam('folderModuleType', false);
  readonly folder = signal<AttachmentFolderDetailDto | undefined>(undefined);
  readonly isRootFolder = computed(() => !!this.folder() && !this.folder()?.parentId);
  readonly subFolders = signal<AttachmentFolderItemDto[] | undefined>(undefined);
  readonly moduleType = computed(() => this.folder()?.folderModuleType ?? this.folderModuleType());
  readonly targetLabel = computed(() => this.moduleType() === 'TGA_DOCUMENTS' ? 'TGAs' : 'Gebäuden');
  readonly showVisibility = computed(() => this.moduleType() === 'TGA_DOCUMENTS' && !this.folder()?.parentId);
  isEditing = false;
  firstSubmit = false;
  form?: FormGroup;
  id: string | null = null;

  saving$ = new BehaviorSubject(false);

  constructor() {
    inject(ActivatedRoute).paramMap.subscribe((params) => {
      const id = this.id = params.get('id');
      if (id) {
        if (id === 'new') {
          this.isEditing = true;
          this.initForm();
        } else {
          this.attachmentFolderService.findById(id).subscribe((folder) => {
            this.folder.set(folder);
            this.initForm(folder);
            this.form?.disable();
            if (!folder.parentId) {
              this.loadSubFolders(id);
            }
          });
        }
      } else {
        this.router.navigate(['attachmentFolder']);
      }
    });
  }

  initForm(dto?: AttachmentFolderDetailDto): void {
    this.form = new FormGroup({
      id: new FormControl(dto?.id),
      name: new FormControl(dto?.name, [Validators.required]),
      color: new FormControl(dto?.color, [Validators.required]),
      description: new FormControl(dto?.description),
      publicFolder: new FormControl(dto?.publicFolder ?? true),
    });
  }

  private async loadSubFolders(parentId: string): Promise<void> {
    try {
      this.subFolders.set(await firstValueFrom(this.attachmentFolderService.findAll({ parentId })));
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Unterordner konnten nicht geladen werden.', 'Fehler');
    }
  }

  hasError(controlName: string, errorName = 'required'): boolean {
    const control = this.form?.get(controlName);
    return this.isEditing && this.firstSubmit && (control?.hasError(errorName) ?? false) && ((control?.touched ?? false) || (control?.dirty ?? false));
  }

  onEdit(): void {
    this.isEditing = true;
    this.form?.enable();
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.form?.markAsTouched();

    if (!this.form?.valid) {
      this.toastrService.danger('Ordner kann nicht gespeichert werden, es gibt noch Fehler.', 'Ordner');
      return;
    }

    try {
      this.saving$.next(true);

      const folderDto = this.form?.value as AttachmentFolderMergeDto;
      folderDto.ownerTenantId = this.tenantId() ?? undefined;
      folderDto.folderModuleType =
        (this.moduleType() as AttachmentFolderMergeDto.FolderModuleTypeEnum | null) ?? 'BUILDING_DOCUMENTS';
      const folder = await firstValueFrom(this.attachmentFolderService.merge(folderDto));
      this.folder.set(folder);
      this.isEditing = false;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['attachmentFolder', folder.id], {
          queryParams: { tenantId: this.tenantId(), folderModuleType: this.moduleType() ?? undefined },
        }).then(() => {
          this.toastrService.success('Ordner wurde erfolgreich gespeichert', 'Ordner');
        });
      });
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Ordner konnte nicht gespeichert werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  async onDelete(): Promise<void> {
    const message = await this.buildDeleteMessage();
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Ordner löschen',
        message,
      },
    }).onClose.subscribe({
      next: (result) => {
        if (result) {
          this.attachmentFolderService.deleteById(this.form?.value.id).subscribe(() => {
            this.router.navigate(['tenant', this.tenantId()], this.tenantNavigationExtras());
            this.toastrService.success('Ordner wurde erfolgreich gelöscht', 'Ordner');
          });
        }
      }, error: () => {
        this.toastrService.danger('Beim Löschen des Ordner ist ein Fehler aufgetreten', 'Ordner');
      },
    });
  }

  private async buildDeleteMessage(): Promise<string> {
    const question = 'Bist du sicher, dass du den Ordner löschen willst?';
    if (!this.isRootFolder() || !this.id) {
      return question;
    }
    const cascade = (subFolders: string) =>
      `${question} ${subFolders} werden inklusive aller enthaltenen Dokumente in allen ${this.targetLabel()} gelöscht.`;
    try {
      const subFolderCount = await firstValueFrom(this.attachmentFolderService.getCount({ parentId: this.id }));
      return subFolderCount > 0 ? cascade(`Die ${subFolderCount} definierten Unterordner`) : question;
    } catch (e) {
      console.error(e);
      return cascade('Alle definierten Unterordner');
    }
  }

  async openCreateSubFolderDialog(): Promise<void> {
    const result = await firstValueFrom(this.dialogService.open(SubFolderDialogComponent, {
      context: { header: 'Unterordner anlegen' },
    }).onClose);
    if (!result || !this.id) {
      return;
    }
    try {
      this.saving$.next(true);
      const created = await firstValueFrom(this.attachmentFolderService.merge({
        parentId: this.id,
        name: result.name,
        color: result.color,
      }));
      this.subFolders.update((subFolders) => [...(subFolders ?? []), created]);
      this.toastrService.success('Unterordner wurde in allen ' + this.targetLabel() + ' angelegt', 'Ordner');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Unterordner konnte nicht angelegt werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  async openRenameSubFolderDialog(subFolder: AttachmentFolderItemDto): Promise<void> {
    const result = await firstValueFrom(this.dialogService.open(SubFolderDialogComponent, {
      context: { header: 'Unterordner bearbeiten', name: subFolder.name, color: subFolder.color },
    }).onClose);
    if (!result) {
      return;
    }
    try {
      this.saving$.next(true);
      const updated = await firstValueFrom(this.attachmentFolderService.merge({
        id: subFolder.id,
        name: result.name,
        color: result.color,
      }));
      this.subFolders.update((subFolders) =>
        (subFolders ?? []).map((sub) => sub.id === updated.id ? updated : sub));
      this.toastrService.success('Unterordner wurde erfolgreich gespeichert', 'Ordner');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Unterordner konnte nicht gespeichert werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  confirmDeleteSubFolder(subFolder: AttachmentFolderItemDto): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Unterordner löschen?',
        message: `Der Unterordner "${subFolder.name}" wird inklusive aller enthaltenen Dokumente in allen ${this.targetLabel()} gelöscht.`,
      },
    }).onClose.subscribe({
      next: async (result) => {
        if (!result) {
          return;
        }
        try {
          this.saving$.next(true);
          await firstValueFrom(this.attachmentFolderService.deleteById(subFolder.id));
          this.subFolders.update((subFolders) => (subFolders ?? []).filter((sub) => sub.id !== subFolder.id));
          this.toastrService.success('Unterordner wurde erfolgreich gelöscht', 'Ordner');
        } catch (e) {
          console.error(e);
          this.toastrService.danger('Unterordner konnte nicht gelöscht werden.', 'Fehler');
        } finally {
          this.saving$.next(false);
        }
      },
    });
  }

  async cancel(): Promise<void> {
    await this.router.navigate(['tenant', this.tenantId()], this.tenantNavigationExtras());
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['tenant', this.tenantId()], this.tenantNavigationExtras());
  }

  private tenantNavigationExtras(): NavigationExtras {
    const tenantType = this.moduleType() === 'TGA_DOCUMENTS' ? 'internal' : 'external';
    return { queryParams: { tenantType } };
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  protected readonly FormGroup = FormGroup;
}
